import { useParams } from 'react-router-dom';
import useEventStatus from '../../hooks/useEventStatus';
import DownloadApp from '../../components/DownloadApp';
import './EventDetail.css';

const formatDate = (value) => {
  if (!value) return '';
  // Parse calendar dates locally so they don't shift with the viewer's timezone.
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  });
};

const formatTime = (value) => {
  if (!value) return '';
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!match) return value;
  const hours = Number(match[1]);
  return `${hours % 12 || 12}:${match[2]} ${hours >= 12 ? 'PM' : 'AM'}`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const EventDetail = () => {
  const { event_code } = useParams();
  const { data: event, loading, error, refetch } = useEventStatus(event_code);
  const location = event?.location || [event?.city, event?.state].filter(Boolean).join(', ');
  const host = event?.created_by;
  const guests = event?.guest_list_size ?? event?.guest_count;
  const status = event?.event_status || event?.status;
  const date = formatDate(event?.event_date || event?.date) || [
    event?.event_date_day, event?.event_date_date, event?.event_date_month, event?.event_date_year,
  ].filter(Boolean).join(' ');
  const hasGuests = guests !== undefined && guests !== null && guests !== '';

  return (
    <main className="event-detail-page">
      <div className="event-detail-container">
        <p className="event-detail-eyebrow">CLICK <span>/</span> EVENT DETAILS</p>
        <article className="event-detail-card" aria-busy={loading}>
          {loading && <div className="event-detail-feedback" role="status">
            <span className="event-detail-loader" aria-hidden="true" />
            <h1>Getting your event ready</h1><p>Loading event details…</p>
          </div>}
          {error && <div className="event-detail-feedback" role="alert">
            <h1>Unable to load this event</h1><p>{error}</p>
            <button type="button" onClick={refetch}>Try again</button>
          </div>}
          {event && <div className="event-detail-layout">
            <div className="event-detail-visual">
              {event.event_image ? <img className="event-detail-image" src={event.event_image} alt={event.title || 'Event'} />
                : <div className="event-detail-placeholder" aria-hidden="true"><span>✦</span><p>Good times.<br />Great memories.</p></div>}
              <div className="event-detail-visual-caption"><span aria-hidden="true">✦</span> A moment worth remembering</div>
            </div>
            <div className="event-detail-content">
              {status && <span className="event-detail-status"><span aria-hidden="true" />{String(status).replace(/_/g, ' ')}</span>}
              <h1>{event.title || 'Event details'}</h1>
              {event.sub_title && event.sub_title !== event.description && <p className="event-detail-subtitle">{event.sub_title}</p>}
              {host?.name && <div className="event-detail-host">
                {host.profile_pic ? <img src={host.profile_pic} alt="" /> : <span className="event-detail-avatar" aria-hidden="true">{host.name.charAt(0)}</span>}
                <div><span>Hosted by</span><strong>{host.name}</strong></div>
              </div>}
              <dl className="event-detail-facts">
                {(date || event.event_time || event.time) && <div><dt>When</dt><dd>{date && <strong>{date}</strong>}{(event.event_time || event.time) && <span>{formatTime(event.event_time || event.time)}</span>}
                  {event.event_end_date && <span className="event-detail-secondary">Ends {formatDate(event.event_end_date)}{event.event_end_time && ` · ${formatTime(event.event_end_time)}`}</span>}
                </dd></div>}
                {location && <div><dt>Where</dt><dd><strong>{location}</strong><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`} target="_blank" rel="noopener noreferrer">Get directions <span aria-hidden="true">↗</span><span className="event-detail-sr-only"> (opens in a new tab)</span></a></dd></div>}
              </dl>
              {event.description && <section className="event-detail-about"><h2>About the event</h2><p>{event.description}</p></section>}
              {(event.image_upload_deadline || event.reveal_datetime || event.reveal_date) && <section className="event-detail-photo-note"><h2>Your event photos</h2>
                {event.image_upload_deadline && <p>Upload photos by {formatDateTime(event.image_upload_deadline)}</p>}
                {(event.reveal_datetime || event.reveal_date) && <p>Photos reveal {formatDateTime(event.reveal_datetime || event.reveal_date)}</p>}
              </section>}
            </div>
          </div>}
        </article>
        <DownloadApp />
      </div>
    </main>
  );
};

export default EventDetail;
